import { FormEvent, KeyboardEvent, RefObject } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@unlikelyai-magic/utils";

/**
 * ChatInput component for the message input area
 */
interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  isDisabled?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
}

export const ChatInput = ({
  value,
  onChange,
  onSubmit,
  isLoading,
  isDisabled = false,
  inputRef,
}: ChatInputProps) => {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (value.trim() && !isLoading && !isDisabled) {
      onSubmit();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="border-t border-brand-border p-4">
      <form onSubmit={handleSubmit}>
        <div className="flex items-center gap-3 rounded-lg border border-brand-border bg-brand-card px-4 py-3">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isDisabled ? "Chat is complete" : "Send a message..."}
            className="flex-1 bg-transparent text-sm text-brand-foreground placeholder-brand-muted outline-none"
            disabled={isLoading || isDisabled}
          />
          <button
            type="submit"
            disabled={!value.trim() || isLoading || isDisabled}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
              value.trim() && !isLoading && !isDisabled
                ? "bg-brand-primary text-white hover:bg-brand-primary-hover"
                : "bg-brand-primary/10 text-brand-muted"
            )}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
